import { useFormContext } from '@calendly/form';
import { object } from '@calendly/form/validation';
import { I18n } from '@calendly/i18n';
import { Button } from '@calendly/ui/components/button';
import { IconButton } from '@calendly/ui/components/icon-button';
import { CloseIcon } from '@calendly/ui/components/icons';
import { BodyText, TitleText } from '@calendly/ui/components/typography';
import { Formik, useFormikContext } from 'formik';
import { useEffect, useRef } from 'react';
import { buildNameNotificationSchema } from 'components/event_type_edit';
import { useEventType } from 'features/event_type_provider';
import { isAnyLocksEnabled } from 'services/event_type_locking';
import { ConfirmationTypeSettingsMenu, getConfirmationTypeForSectionKey, getConfirmationTypeSectionTitle, isConfirmationTypeSectionKey } from './confirmation_type_settings_menu';
import { getCrudNotificationSectionConfig } from './notification_section_config';
import { useSelectedSections, useSelectedSectionsActions } from '../../../components/selected_sections';
import { getInitialValues } from '../../notifications/subsections/config';
import { NotificationFields } from '../../notifications/subsections/notification_fields';

/* Load after @calendly/rich-text-editor so the drawer toolbar overrides win the cascade. */
import '../../../../event_type_editor_rich_text_overrides.css';

// Match other CRUD tab panels (notifications list, confirmation, payments): fill the
// content column with the same gap rhythm and no drawer inset/chrome. Slides in from
// the left on open; close unmounts immediately with no exit animation.
const panelCss = "p16q5vra";
const headerCss = "h1uerne";
const headingCss = "hlfl9td";
const formCss = "f5583kg";
const footerCss = "f5wjqa8";
const headerActionsCss = "h1hbg3if";

// Prevent Enter from submitting the parent event-type <form> while editing inline.
const shouldIgnoreEnterSubmit = target => Boolean(target.closest('button, textarea, a, [contenteditable="true"], [role="listbox"], [role="option"]'));
const NotificationEditorForm = ({
  kind,
  drawerEventType,
  description,
  showConfirmationTypeSettings,
  onCancel
}) => {
  const {
    submitForm
  } = useFormikContext();
  const rootRef = useRef(null);
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    const handleKeyDown = event => {
      if (event.key !== 'Enter' || event.defaultPrevented) return;
      if (shouldIgnoreEnterSubmit(event.target)) return;
      event.preventDefault();
      submitForm();
    };
    root.addEventListener('keydown', handleKeyDown);
    return () => root.removeEventListener('keydown', handleKeyDown);
  }, [submitForm]);
  return <div className={formCss} ref={rootRef}>
      <NotificationFields eventType={drawerEventType} kind={kind} description={description} hideSharedSettings hideDescription hideConfirmationTypeSwitch={showConfirmationTypeSettings} hideStatus useMeetingTimingLabels immediateTimingAsSelect useDeleteTimingIcon useUpgradeInlineBanner useUpdatedToolbarActions openVariablesDropdownOnLeft alignIconOnlyVariablesWithInput />
      <footer className={footerCss}>
        <Button appearance="ghost" type="button" onClick={onCancel}>
          {I18n.t('publisher.action.cancel')}
        </Button>
        <Button appearance="secondary-outline" type="button" onClick={submitForm}>
          {I18n.t('publisher.action.done')}
        </Button>
      </footer>
    </div>;
};

// Inline editor for a single notification's content (subject/body/timings) in the
// CRUD notifications tab. Replaces the notifications list while the Notifications
// tab stays active. Reuses NotificationFields, but instead of PATCHing on save it
// stages form-shaped values into the parent root form under
// `notificationDrafts[kind]` and closes — persistence is owned by the parent CRUD
// Save. Uses Formik without a nested <form> so it can render inside the root form.
export const CrudNotificationDrawer = () => {
  const {
    selectedSections,
    closeCurrentSection
  } = useSelectedSections();
  const {
    swapCurrentSection
  } = useSelectedSectionsActions();
  const parentForm = useFormContext();
  const eventType = useEventType();
  const panelRef = useRef(null);
  const selected = selectedSections?.[0];
  const config = selected ? getCrudNotificationSectionConfig(selected.key, eventType) : null;
  const drawerKind = config?.kind ?? null;
  const etLocked = !eventType.isMETParent && isAnyLocksEnabled(eventType, ['v3_notifications']);
  const showConfirmationTypeSettings = isConfirmationTypeSectionKey(selected?.key) && !eventType.isGroup && !etLocked;
  useEffect(() => {
    if (!drawerKind) return undefined;
    const handleKeyDown = event => {
      if (event.key === 'Escape') closeCurrentSection();
    };
    document.addEventListener('keydown', handleKeyDown);
    panelRef.current?.focus();
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [drawerKind, closeCurrentSection]);
  if (!config) return null;
  const {
    kind,
    drawerEventType,
    description
  } = config;
  const stagedDraft = parentForm.values?.notificationDrafts?.[kind];
  const initialValues = stagedDraft || getInitialValues(drawerEventType, kind);
  const validationSchema = object({
    [kind]: buildNameNotificationSchema(initialValues[kind])
  });
  const handleApply = values => {
    // Confirmation type is previewed via section swap; commit it with the draft
    // so Cancel / Escape leave the parent form unchanged.
    if (isConfirmationTypeSectionKey(selected.key)) {
      const notificationType = getConfirmationTypeForSectionKey(selected.key);
      if (notificationType) {
        parentForm.setFieldValue('notificationType', notificationType);
      }
    }
    parentForm.setFieldValue(`notificationDrafts.${kind}`, values);
    closeCurrentSection();
  };
  const handleConfirmationTypeSelect = sectionKey => {
    swapCurrentSection({
      key: sectionKey,
      title: getConfirmationTypeSectionTitle(sectionKey)
    });
  };
  return <section className={panelCss} data-testid="crud-notification-drawer" data-component="crud-notification-drawer" aria-label={selected.title} tabIndex={-1} ref={panelRef}>
      <header className={headerCss}>
        <div className={headingCss}>
          <TitleText level={2} size="large" as="h2">
            {selected.title}
          </TitleText>
          {description && <BodyText color="muted" size="large">
              {description}
            </BodyText>}
        </div>
        <div className={headerActionsCss}>
          {showConfirmationTypeSettings ? <ConfirmationTypeSettingsMenu selectedSectionKey={selected.key} onSelect={handleConfirmationTypeSelect} /> : null}
          <IconButton appearance="ghost" size="medium" onClick={closeCurrentSection} title={I18n.t('publisher.action.close')} aria-label={I18n.t('publisher.action.close')}>
            <CloseIcon />
          </IconButton>
        </div>
      </header>
      <Formik key={kind} initialValues={initialValues} validationSchema={validationSchema} validateOnChange={false} validateOnBlur={false} onSubmit={handleApply}>
        <NotificationEditorForm kind={kind} drawerEventType={drawerEventType} description={description} showConfirmationTypeSettings={showConfirmationTypeSettings} onCancel={closeCurrentSection} />
      </Formik>
    </section>;
};

require("./crud_notification_drawer.wyw-in-js.css!=!../../../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./crud_notification_drawer.js");